import { IssueUserInfo } from 'pages/Desk/issues/types';
import { UserResult } from 'pages/Desk/issues/UserResult';
import { Fragment, useMemo, useRef } from 'react';
import { ResultsLoadingIndicator } from 'shared/loading_display/ResultsLoadingIndicator';
import { TriggeredPopupAvailableHeight } from 'shared/triggered_popup/TriggeredPopup';
import { VisibilityProvider } from 'shared/visibility_context/VisibilityContext';
import { VisibilityTrigger } from 'shared/visibility_context/VisibilityTrigger';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { splitSearchTextIntoTokens } from 'utils/query_v3';

const MaxPopupHeight = '32.5rem';

const ResultsVisibilityProvider = styled(VisibilityProvider)`
    box-sizing: border-box;
    max-height: min(${TriggeredPopupAvailableHeight}, ${MaxPopupHeight});
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
`;

const Message = styled.div`
    padding: 1rem 1.5rem;
    font-size: 1.4rem;
    color: ${tokens.text.secondary};
`;

const StyledResultsLoadingIndicator = styled(ResultsLoadingIndicator)`
    padding: 0.5rem;
`;

type UsersPopupContentProps = Readonly<{
    users: IssueUserInfo[] | undefined;
    searchText: string;
    isLoading: boolean;
    hasMore: boolean;
    highlightedIndex: number;
    onBottom: () => void;
    onUserSelect: (user: IssueUserInfo) => void;
}>;

export function UsersPopupContent({
    users,
    searchText,
    isLoading,
    hasMore,
    highlightedIndex,
    onBottom,
    onUserSelect,
}: UsersPopupContentProps) {
    const atBottomRef = useRef(false);
    const onVisibilityChange = (isVisible: boolean) => {
        atBottomRef.current = isVisible;
        if (users && isVisible && hasMore && !isLoading) {
            onBottom();
        }
    };

    const searchTokens = useMemo(
        () => splitSearchTextIntoTokens(searchText),
        [searchText],
    );

    return (
        <>
            {!users && <Message>Search for users</Message>}
            {users && users.length === 0 && <Message>No users found</Message>}
            {users && users.length > 0 && (
                <ResultsVisibilityProvider aria-label="User search results">
                    {users.map((user, index) => (
                        <Fragment key={user.id}>
                            {index === users.length - 1 && (
                                <VisibilityTrigger onChange={onVisibilityChange} />
                            )}
                            <UserResult
                                user={user}
                                searchTokens={searchTokens}
                                isHighlighted={index === highlightedIndex}
                                autoScroll
                                onClick={onUserSelect}
                            />
                        </Fragment>
                    ))}
                    {isLoading && atBottomRef.current && (
                        <StyledResultsLoadingIndicator />
                    )}
                </ResultsVisibilityProvider>
            )}
        </>
    );
}
